import { watch, type Component } from 'vue';

// PRODUCT-26110 Phase 2B2: 'vue-router/types' is a vue-router-3-only subpath, not
// present in v4's `exports` map at all -- module-not-found, not just a type mismatch.
// `RouteConfig` -> `RouteRecordRaw`.
import noop from 'lodash/noop';
import type { RouteRecordRaw } from 'vue-router';

import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import Tracker from '@evidentid/tracker';
import delay from '@evidentid/universal-framework/delay';
import withPropsInjected from '@evidentid/vue-commons/decorators/withPropsInjected';

import { createModuleBuilder } from '../../app';

import { commonErrors } from './commonErrors';
import ErrorWrapper from './components/ErrorWrapper.vue';
import { createRouteErrorSync } from './routeErrorSync';
import { usePersistingErrorStore } from './store';
import { ErrorDefinition } from './types';

export function createPersistingErrorModule() {
    return createModuleBuilder()
        .demandOptions<{
        errorDefinitions?: Record<string, ErrorDefinition>;
        views: { Error: Component };
        temporaryErrors?: string[];
    }>()
        .demandContext<{
        tracker?: Tracker<any>;
        logger?: Logger<any>;
    }>()
        .registerRoutes((app) => [
            {
                path: '/error/application',
                name: 'error.application',
                component: withPropsInjected(ErrorWrapper, {
                    Component: app.options.views.Error,
                }),
                meta: { title: 'Error' },
            },
            {
                path: '/error/:reason',
                name: 'error',
                component: withPropsInjected(ErrorWrapper, {
                    Component: app.options.views.Error,
                }),
                meta: { title: 'Error' },
            },
            {
                path: '/:rpId/error/:reason',
                name: 'error.rp',
                component: withPropsInjected(ErrorWrapper, {
                    Component: app.options.views.Error,
                }),
                meta: { title: 'Error' },
            },
            {
                path: '/error',
                name: 'error.generic',
                component: Error,
                meta: { title: 'Error' },
            },
        ] as RouteRecordRaw[])
        .injectVue((app) => ({
            $errorDefinitions: {
                ...commonErrors,
                ...app.options.errorDefinitions,
            },
        }))
        .execute(async (app) => {
            // Clean up temporary errors on initialization
            const temporaryErrors = app.options.temporaryErrors || [];

            // Ensure the current route is already resolved
            // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under vue-router 4.
            while (!app.router.currentRoute.value.name) {
                await delay(0);
            }

            // Retrieve route details
            const route = app.router.currentRoute.value;
            const next = typeof route.query.next === 'string' ? route.query.next : '/';

            // Clean up temporary errors
            if (
                (route.name === 'error' || route.name === 'error.rp') &&
                // vue-router 4 types repeated params as `string | string[]`, stricter
                // than vue-router 3 -- this route has no repeated `reason` segment, so
                // `as string` matches ed2202df8's established narrowing.
                (next || temporaryErrors.includes(route.params.reason as string))
            ) {
                app.router.replace(next).catch(noop);
            }
        })
        .execute((app) => {
            const { tick, handleAfterEach } = createRouteErrorSync(app);
            const errorStore = usePersistingErrorStore(app.getPinia());
            watch(() => errorStore.error, tick);
            app.router.afterEach(handleAfterEach);
            tick();
        })

        // Track events
        .execute((app) => {
            const errorStore = usePersistingErrorStore(app.getPinia());
            watch(() => errorStore.error, (error) => {
                const reason = error && 'reason' in error ? error.reason : null;
                const xhr = error && (('xhr' in error && error.xhr) || ('xhrObj' in error && error.xhrObj));

                // Lift error when it is a JavaScript error
                if (error && error instanceof Error) {
                    // Do not capture cancelled network requests as errors in Sentry, as we can't do anything about it
                    // It needs to be inside, otherwise TypeScript will detect `error` type incorrectly
                    if ((error as any).reason !== 'cancelled-network-request') {
                        setTimeout(() => {
                            throw error;
                        });
                    }
                }

                if (app.tracker) {
                    app.tracker.track({
                        name: error ? 'Error shown' : 'Error hidden',
                        description: error ? `Error shown: ${reason || 'unknown'}` : 'Error hidden',
                        message: error?.message,
                        reason,
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        level: error ? Severity.warning : Severity.info,
                        category: 'error',
                        message: error ? 'Error shown' : 'Error hidden',
                        data: {
                            reason: reason || '',
                            message: error?.message || '',
                            xhr: Boolean(xhr),
                            native: Boolean(error && error instanceof Error),
                        },
                    });
                }
            });
        })

        .end();
}

// Compat shim: legacy callers pass a Vuex store/dispatch scope — ignored now
// that the persisting-error module is a Pinia store carrying its own actions.
export const getPersistingErrorActions = (_scope?: unknown) => usePersistingErrorStore();
export type PersistingErrorModule = ReturnType<typeof createPersistingErrorModule>;
